"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { slugify } from "@papier/shared"
import { Button } from "@papier/ui/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@papier/ui/components/ui/dialog"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@papier/ui/components/ui/dropdown-menu"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@papier/ui/components/ui/form"
import { Input } from "@papier/ui/components/ui/input"
import { cn } from "@papier/ui/lib/utils"
import { Refresh as Loader2, TrashBinTrash as Trash2Icon, CloudUpload as UploadCloudIcon } from "@papier/ui/components/icons"
import {
    type ComponentProps,
    useContext,
    useMemo,
    useRef,
    useState
} from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { AuthUIContext } from "../../lib/auth-ui-provider"
import { fileToBase64, resizeAndCropImage } from "../../lib/image-utils"
import { getLocalizedError } from "../../lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { OrganizationLogo } from "./organization-logo"

export interface CreateOrganizationDialogProps extends ComponentProps<
    typeof Dialog
> {
    className?: string
    classNames?: SettingsCardClassNames
    localization?: AuthLocalization
    onSetActive?: () => Promise<void>
    onCloseAutoFocus?: ComponentProps<typeof DialogContent>["onCloseAutoFocus"]
    onOpenChange?: (open: boolean) => void
}

export function CreateOrganizationDialog({
    className,
    classNames,
    localization: localizationProp,
    onSetActive,
    onCloseAutoFocus,
    onOpenChange,
    ...props
}: CreateOrganizationDialogProps) {
    const {
        authClient,
        localization: contextLocalization,
        organization: organizationOptions,
        toast
    } = useContext(AuthUIContext)

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const [logo, setLogo] = useState<string | null>(null)
    const [logoPending, setLogoPending] = useState(false)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const openFileDialog = () => fileInputRef.current?.click()

    const formSchema = z.object({
        logo: z.string().optional(),
        name: z
            .string()
            .trim()
            .min(1, { message: "Name is required" })
            .max(300, { message: "Name must be at most 300 characters long" })
            .regex(/^[a-zA-Z0-9\s]+$/, {
                message: "Name must contain only letters, numbers, and spaces"
            })
            .transform((val) => val.replace(/\s+/g, " "))
    })

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            logo: "",
            name: ""
        }
    })

    const isSubmitting = form.formState.isSubmitting

    const handleLogoChange = async (file: File) => {
        if (!organizationOptions?.logo) return

        setLogoPending(true)

        try {
            const resizedFile = await resizeAndCropImage(
                file,
                crypto.randomUUID(),
                organizationOptions.logo.size,
                organizationOptions.logo.extension
            )

            let image: string | undefined | null

            if (organizationOptions?.logo.upload) {
                image = await organizationOptions.logo.upload(resizedFile)
            } else {
                image = await fileToBase64(resizedFile)
            }

            setLogo(image || null)
            form.setValue("logo", image || "")
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })
        }

        setLogoPending(false)
    }

    const deleteLogo = async () => {
        setLogoPending(true)

        const currentUrl = logo || undefined
        if (currentUrl && organizationOptions?.logo?.delete) {
            await organizationOptions.logo.delete(currentUrl)
        }

        setLogo(null)
        form.setValue("logo", "")
        setLogoPending(false)
    }

    async function onSubmit({ name, logo }: z.infer<typeof formSchema>) {
        try {
            const organization = await authClient.organization.create({
                name,
                slug: slugify(name),
                logo,
                fetchOptions: { throw: true }
            })

            await authClient.organization.setActive({
                organizationId: organization.id
            })

            toast({
                variant: "success",
                message: localization.CREATE_ORGANIZATION_SUCCESS
            })

            await onSetActive?.()

            onOpenChange?.(false)
            setLogo(null)
            form.reset()
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })
        }
    }

    return (
        <Dialog onOpenChange={onOpenChange} {...props}>
            <DialogContent
                className={cn(className, classNames?.dialog?.content)}
                onCloseAutoFocus={onCloseAutoFocus}
            >
                <DialogHeader className={classNames?.dialog?.header}>
                    <DialogTitle
                        className={cn("text-lg md:text-xl", classNames?.title)}
                    >
                        {localization.CREATE_ORGANIZATION}
                    </DialogTitle>

                    <DialogDescription
                        className={cn(
                            "text-xs md:text-sm",
                            classNames?.description
                        )}
                    >
                        {localization.ORGANIZATIONS_INSTRUCTIONS}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form
                        onSubmit={(event) =>
                            void form.handleSubmit(onSubmit)(event)
                        }
                        className="space-y-6"
                    >
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {localization.ORGANIZATION_NAME}
                                    </FormLabel>

                                    <FormControl>
                                        <Input
                                            placeholder={
                                                localization.ORGANIZATION_NAME_PLACEHOLDER
                                            }
                                            {...field}
                                        />
                                    </FormControl>

                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {organizationOptions?.logo && (
                            <FormField
                                control={form.control}
                                name="logo"
                                render={() => (
                                    <FormItem>
                                        <input
                                            ref={fileInputRef}
                                            accept="image/*"
                                            disabled={logoPending}
                                            hidden
                                            type="file"
                                            onChange={(e) => {
                                                const file =
                                                    e.target.files?.item(0)
                                                if (file)
                                                    void handleLogoChange(file)
                                                e.target.value = ""
                                            }}
                                        />

                                        <FormLabel>
                                            {localization.LOGO}
                                        </FormLabel>

                                        <div className="flex items-center gap-4">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        className="size-fit rounded-full"
                                                        size="icon"
                                                        type="button"
                                                        variant="ghost"
                                                    >
                                                        <OrganizationLogo
                                                            className="size-16"
                                                            isPending={
                                                                logoPending
                                                            }
                                                            localization={
                                                                localization
                                                            }
                                                            organization={{
                                                                name: form.watch(
                                                                    "name"
                                                                ),
                                                                logo
                                                            }}
                                                        />
                                                    </Button>
                                                </DropdownMenuTrigger>

                                                <DropdownMenuContent
                                                    align="start"
                                                    onCloseAutoFocus={(e) =>
                                                        e.preventDefault()
                                                    }
                                                >
                                                    <DropdownMenuItem
                                                        onClick={openFileDialog}
                                                        disabled={logoPending}
                                                    >
                                                        <UploadCloudIcon />

                                                        {
                                                            localization.UPLOAD_LOGO
                                                        }
                                                    </DropdownMenuItem>

                                                    {logo && (
                                                        <DropdownMenuItem
                                                            onClick={() =>
                                                                void deleteLogo()
                                                            }
                                                            disabled={
                                                                logoPending
                                                            }
                                                            variant="destructive"
                                                        >
                                                            <Trash2Icon />

                                                            {
                                                                localization.DELETE_LOGO
                                                            }
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>

                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        <DialogFooter className={classNames?.dialog?.footer}>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange?.(false)}
                                className={cn(
                                    classNames?.button,
                                    classNames?.outlineButton
                                )}
                            >
                                {localization.CANCEL}
                            </Button>

                            <Button
                                type="submit"
                                className={cn(
                                    classNames?.button,
                                    classNames?.primaryButton
                                )}
                                disabled={isSubmitting}
                            >
                                {isSubmitting && (
                                    <Loader2 className="animate-spin" />
                                )}

                                {localization.CREATE_ORGANIZATION}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
